import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import { alpha } from "@mui/material/styles";

const CYCLE_LABELS = {
  monthly: "Mensal",
  trimestral: "Trimestral",
  annual: "Anual",
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value || 0);
};

const formatLimit = (value, suffix) => {
  if (value === -1) return "Ilimitado";
  if (value === 0) return "-";
  return suffix ? `${value}${suffix}` : String(value);
};

export default function PlansComparison({
  plans,
  currentPlanId,
  subscriptionNeedsRenewal = false,
  onUpgrade,
}) {
  const [cycle, setCycle] = useState("monthly");

  if (!plans || plans.length === 0) return null;

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Planos Disponíveis
        </Typography>
        <ToggleButtonGroup
          value={cycle}
          exclusive
          onChange={(_, v) => v && setCycle(v)}
          size="small"
        >
          {Object.entries(CYCLE_LABELS).map(([key, label]) => (
            <ToggleButton key={key} value={key} sx={{ textTransform: "none", px: 2 }}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: `repeat(${Math.min(plans.length, 4)}, 1fr)`,
          },
          gap: 2,
        }}
      >
        {plans.map((plan) => {
          const isCurrent = plan.plan_id === currentPlanId;
          const pricing = plan.pricing?.[cycle];
          const discount = pricing?.discount || 0;

          return (
            <Box
              key={plan.plan_id}
              sx={{
                p: 3,
                borderRadius: 3,
                border: (theme) =>
                  `2px solid ${isCurrent ? theme.palette.primary.main : theme.palette.divider}`,
                backgroundColor: isCurrent
                  ? (theme) => alpha(theme.palette.primary.main, 0.03)
                  : "background.paper",
                position: "relative",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {isCurrent && (
                <Chip
                  icon={<StarIcon sx={{ fontSize: "14px !important" }} />}
                  label="Plano Atual"
                  color="primary"
                  size="small"
                  sx={{
                    position: "absolute",
                    top: -12,
                    right: 16,
                    fontWeight: 600,
                    fontSize: "0.7rem",
                  }}
                />
              )}

              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {plan.name}
              </Typography>

              {plan.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5, minHeight: 40 }}
                >
                  {plan.description}
                </Typography>
              )}

              <Box sx={{ mt: 2 }}>
                {plan.price_monthly > 0 ? (
                  <>
                    <Stack direction="row" alignItems="baseline" spacing={0.5}>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {formatCurrency(pricing?.price || plan.price_monthly)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        /{CYCLE_LABELS[cycle]?.toLowerCase()}
                      </Typography>
                    </Stack>
                    {discount > 0 && (
                      <Chip
                        label={`-${discount}%`}
                        color="success"
                        size="small"
                        sx={{ mt: 0.5, fontWeight: 600 }}
                      />
                    )}
                  </>
                ) : (
                  <Typography variant="h4" sx={{ fontWeight: 700, color: "success.main" }}>
                    Gratuito
                  </Typography>
                )}
              </Box>

              <Stack spacing={1} sx={{ mt: 3, flex: 1 }}>
                <LimitRow label="Restaurantes" value={formatLimit(plan.max_restaurants)} />
                <LimitRow label="Centrais" value={formatLimit(plan.max_delivery_centers)} />
                <LimitRow label="Motoristas" value={formatLimit(plan.max_drivers)} />
                <LimitRow label="Custo Simples" value={plan.enable_cost_control ? formatLimit(plan.max_cost_entries_per_month, "/mês") : "-"} />
                <LimitRow label="Etiquetas" value={plan.enable_labels ? formatLimit(plan.max_labels_per_month, "/mês") : "-"} />
                <LimitRow label="Fiscal" value={plan.enable_fiscal ? formatLimit(plan.max_fiscal_tickets_per_month, "/mês") : "-"} />
                <LimitRow label="Uber Direct" value={plan.enable_uber_direct ? formatLimit(plan.max_uber_deliveries_per_month, "/mês") : "-"} />
                <FeatureRow label="Auto Pilot" enabled={plan.enable_auto_pilot} />
                <LimitRow label="WhatsApp" value={plan.enable_whatsapp ? formatLimit(plan.max_whatsapp_instances) : "-"} />
              </Stack>

              <Box sx={{ mt: 3 }}>
                {isCurrent && subscriptionNeedsRenewal ? (
                  <Button
                    fullWidth
                    variant="contained"
                    color="warning"
                    onClick={() => onUpgrade(plan, cycle)}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    Renovar Plano
                  </Button>
                ) : isCurrent ? (
                  <Button
                    fullWidth
                    variant="outlined"
                    disabled
                    sx={{ textTransform: "none" }}
                  >
                    Plano Atual
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => onUpgrade(plan, cycle)}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    {plan.price_monthly === 0 ? "Fazer Downgrade" : "Escolher Plano"}
                  </Button>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function LimitRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
    </Stack>
  );
}

function FeatureRow({ label, enabled }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {enabled ? (
        <CheckIcon sx={{ fontSize: 18, color: "success.main" }} />
      ) : (
        <CloseIcon sx={{ fontSize: 18, color: "grey.400" }} />
      )}
    </Stack>
  );
}
